/**
 * What hg-content's staff API gives and takes for news, stories, legal
 * documents, the apps' settings and images.
 *
 * Every text is a `LocalizedText` (`title`, `body`, a tag's `name`; a
 * slide's `media` is a file a language): strings by language. They are read
 * through `textsOf` (`../lib/texts.ts`), which leaves out an empty one, and
 * written through the inputs below, which the requests take as they are.
 */
import type { components } from "@/shared/api/content.schema";

type Schemas = components["schemas"];

export type News = Schemas["AdminNewsResponse"];
export type Story = Schemas["AdminStoryResponse"];
export type Slide = Schemas["AdminSlideResponse"];
export type Tag = Schemas["AdminTagResponse"];
export type Audience = Schemas["Audience"];
export type ItemState = Schemas["State"];
export type StateCounts = Schemas["StateCountsResponse"];
export type AudiencePreview = Schemas["AudiencePreviewResponse"];
export type Author = Schemas["AuthorResponse"];
export type ImageFile = Schemas["FileResponse"];
export type LegalKind = Schemas["LegalKind"];
export type VersionState = Schemas["VersionState"];
export type VersionSummary = Schemas["VersionSummaryResponse"];
export type Version = Schemas["VersionResponse"];
export type AppSettings = Schemas["AppSettingsResponse"];
export type Gate = Schemas["GateResponse"];
export type Platform = Schemas["Platform"];
export type Consent = Schemas["AdminConsentResponse"];
export type SettingChange = Schemas["SettingChangeResponse"];
export type DocumentVersion = Schemas["DocumentVersionResponse"];

/**
 * A legal document as `GET /v2/admin/legal-documents` gives it: one kind for
 * one market and one app, with how many versions it has (drafts among them;
 * `0`: every draft it had was deleted), the one in effect and the newest.
 */
export type LegalDocument = Schemas["AdminLegalDocumentResponse"];

/** A text by language: `{ uz: "Yangi xizmat", ru: "Новая услуга" }` (hg-content's `LocalizedText`). */
export type Texts = Readonly<Record<string, string>>;

/** Where a news item or a story stands, in the order the tabs show them. */
export const ITEM_STATES = [
  "draft",
  "scheduled",
  "live",
  "expired",
  "archived",
] as const satisfies readonly ItemState[];
export const AUDIENCES = ["client", "specialist"] as const satisfies readonly Audience[];
export const PLATFORMS = ["android", "ios"] as const satisfies readonly Platform[];
export const LEGAL_KINDS = [
  "public_offer",
  "privacy_policy",
  "ai_policy",
  "patient_authorization",
] as const satisfies readonly LegalKind[];
export const VERSION_STATES = [
  "draft",
  "upcoming",
  "current",
  "superseded",
] as const satisfies readonly VersionState[];

/** What an image may be, and how large (`POST /v2/admin/files`): no SVG, which can carry a script. */
export const IMAGE_TYPES = ["image/jpeg", "image/png", "image/webp"] as const;
export const IMAGE_MAX_BYTES = 10 * 1024 * 1024;

/** Where a news item or a story is seen: its market, its apps, its areas (none: the whole market). */
export interface Placement {
  market_code: string;
  audiences: Audience[];
  area_ids: string[];
}

/** A news item as staff write it (`CreateNewsRequest`, its texts typed). */
export interface NewsInput extends Placement {
  title: Texts;
  /** HTML of plain tags and no attributes, in the languages of the title. */
  body: Texts;
  image_file_id: string | null;
  tag_id: string | null;
}

/** What is changed of a news item: what is left out stays; `null` takes an image or a tag off. */
export type NewsChange = Partial<Omit<NewsInput, "market_code">>;

/** A slide as it is sent: kept by its id, or new; a picture per language. */
export interface SlideInput {
  id?: string;
  media: Record<string, string>;
}

/** A story as staff write it (`CreateStoryRequest`, its texts typed). */
export interface StoryInput extends Placement {
  title: Texts;
  sort_order: number;
  icon_file_id: string | null;
  cover_file_id: string | null;
  slides: SlideInput[];
}

export type StoryChange = Partial<Omit<StoryInput, "market_code">>;

/** When an item is seen (`PublishRequest`): left out, as the service says. */
export interface Publication {
  publish_at?: string | null;
  expires_at?: string | null;
}

/** A legal document's next version, as a superadmin writes it. */
export interface VersionInput {
  kind: LegalKind;
  market_code: string;
  audience: Audience;
  title: Texts;
  body: Texts;
}
